import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ProjectArtifact } from '../../types';

interface EditArtifactModalProps {
  artifact: ProjectArtifact;
  onClose: () => void;
  onSubmit: (updateData: {
    artifactType?: string;
    name?: string;
    description?: string;
    isRequired?: boolean;
    data?: any;
  }) => void;
}

const ARTIFACT_TYPES = [
  { value: 'document', label: 'Dokument', description: 'Spezifikationen, Anforderungen, Reports' },
  { value: 'template', label: 'Vorlage', description: 'Wiederverwendbare Templates und Muster' },
  { value: 'resource', label: 'Ressource', description: 'Links, Tools, externe Materialien' },
  { value: 'deliverable', label: 'Lieferobjekt', description: 'Konkrete Projektergebnisse' },
  { value: 'checklist', label: 'Checkliste', description: 'Prüflisten und Qualitätssicherung' }
];

export const EditArtifactModal: React.FC<EditArtifactModalProps> = ({ 
  artifact, 
  onClose, 
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    artifactType: artifact.artifactType,
    name: artifact.name,
    description: artifact.description || '',
    isRequired: artifact.isRequired,
  });

  const [jsonData, setJsonData] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize JSON data from artifact
    setJsonData(JSON.stringify(artifact.data || {}, null, 2));
  }, [artifact]);

  const validateJson = (jsonString: string) => {
    if (!jsonString.trim()) {
      setJsonData('{}');
      setJsonError(null);
      return {};
    }

    try {
      const parsed = JSON.parse(jsonString);
      setJsonError(null);
      return parsed;
    } catch (error) {
      setJsonError('Ungültiges JSON-Format');
      return null;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      return;
    }

    const parsedData = validateJson(jsonData);
    if (jsonError || parsedData === null) {
      return;
    }

    // Only send changed fields
    const updateData: any = {};
    
    if (formData.artifactType !== artifact.artifactType) {
      updateData.artifactType = formData.artifactType;
    }
    
    if (formData.name.trim() !== artifact.name) {
      updateData.name = formData.name.trim();
    }
    
    const newDescription = formData.description.trim() || undefined;
    if (newDescription !== artifact.description) {
      updateData.description = newDescription;
    }
    
    if (formData.isRequired !== artifact.isRequired) {
      updateData.isRequired = formData.isRequired;
    }
    
    if (JSON.stringify(parsedData) !== JSON.stringify(artifact.data)) {
      updateData.data = parsedData;
    }

    onSubmit(updateData);
  };

  const selectedType = ARTIFACT_TYPES.find(type => type.value === formData.artifactType);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Artefakt bearbeiten</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Artifact Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Artefakt-Typ
            </label>
            <div className="grid grid-cols-1 gap-3">
              {ARTIFACT_TYPES.map((type) => (
                <label key={type.value} className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="artifactType"
                    value={type.value}
                    checked={formData.artifactType === type.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, artifactType: e.target.value }))}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{type.label}</div>
                    <div className="text-sm text-gray-500">{type.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder={`Name für ${selectedType?.label}...`}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Beschreibung
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder="Beschreibung des Artefakts..."
            />
          </div>

          {/* Required Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isRequired"
              checked={formData.isRequired}
              onChange={(e) => setFormData(prev => ({ ...prev, isRequired: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isRequired" className="ml-2 text-sm text-gray-900">
              Dieses Artefakt ist erforderlich für den Projektabschluss
            </label>
          </div>

          {/* JSON Data */}
          <div>
            <label htmlFor="jsonData" className="block text-sm font-medium text-gray-700 mb-2">
              Zusätzliche Daten (JSON)
            </label>
            <textarea
              id="jsonData"
              value={jsonData}
              onChange={(e) => {
                setJsonData(e.target.value);
                validateJson(e.target.value);
              }}
              rows={6}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 font-mono text-sm ${
                jsonError ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder='{"file_path": "/path/to/file", "priority": "high"}'
            />
            {jsonError && (
              <p className="mt-1 text-sm text-red-600">{jsonError}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Strukturierte Daten für das Artefakt (z.B. Dateipfade, Prioritäten, etc.)
            </p>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">
              Erstellt: {new Date(artifact.createdAt).toLocaleString('de-DE')}
            </p>
            {artifact.updatedAt && artifact.updatedAt !== artifact.createdAt && (
              <p className="text-xs text-gray-500">
                Zuletzt geändert: {new Date(artifact.updatedAt).toLocaleString('de-DE')}
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={!formData.name.trim() || !!jsonError}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Änderungen speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};